import { observer } from 'mobx-react-lite'
import { CardsFilter, type ICardFilter } from 'shared/ui'
import { truthy } from 'shared/lib'
import {
  ChatbotConversationStatus,
  ChatbotInstructionType,
  type IChatbotConversationStatus,
} from 'entities/Chatbot'
import { useEntitiesContext } from 'entities/EntitiesExport'
import {
  type ChatbotAnalyticDetailStore,
  type ChatbotAnalyticListStore,
} from 'pages/chatbot/pages/chatbotAnalytic/store'

import styles from './styles.module.scss'

export interface IChatbotAnalyticFilterProps {
  details: ChatbotAnalyticDetailStore
  list: ChatbotAnalyticListStore
}

export const ChatbotAnalyticFilter = observer(({ details, list }: IChatbotAnalyticFilterProps) => {
  const { featureFlagsStore, organizationStore } = useEntitiesContext()

  const quietHoursFilter: ICardFilter<IChatbotConversationStatus> | null = organizationStore
    .quietHoursSettings?.is_active
    ? {
        key: ChatbotConversationStatus.QuietHours,
        title: 'Quiet hours',
        amount: details.quietHoursMessagesCount,
        percentage: details.quietHoursMessagesPercent,
        active: list.status === ChatbotConversationStatus.QuietHours,
        tooltipProps: {
          maxWidth: 351,
          label: 'Contacts whose messages are delayed by quiet hours',
          placement: 'top',
        },
      }
    : null

  const allItems: Array<ICardFilter<IChatbotConversationStatus | null> | false> = [
    {
      key: null,
      title: 'Started',
      amount: details.totalCount,
      active: list.status === null,
      tooltipProps: {
        label: 'Contact enrolled to chat with the AI Agent',
        placement: 'top',
      },
    },
    {
      key: ChatbotConversationStatus.InProgress,
      title: 'In Progress',
      amount: details.inProgressCount,
      percentage: details.inProgressPercent,
      active: list.status === ChatbotConversationStatus.InProgress,
      tooltipProps: {
        label: 'Contacts chatting with the AI Agent',
        placement: 'top',
      },
    },
    featureFlagsStore.qualify &&
      details.qualifyEnabled && {
        key: ChatbotConversationStatus.Qualified,
        title: 'Qualified',
        amount: details.qualifiedCount,
        percentage: details.qualifiedPercent,
        active: list.status === ChatbotConversationStatus.Qualified,
        tooltipProps: {
          label: 'Contacts marked as qualified',
          placement: 'top',
        },
      },
    featureFlagsStore.qualify &&
      details.qualifyEnabled && {
        key: ChatbotConversationStatus.Disqualified,
        title: 'Disqualified',
        amount: details.disqualifiedCount,
        percentage: details.disqualifiedPercent,
        active: list.status === ChatbotConversationStatus.Disqualified,
        tooltipProps: {
          label: 'Contacts marked as disqualified',
          placement: 'top',
        },
      },
    featureFlagsStore.qualify &&
      details.qualifyEnabled && {
        key: ChatbotConversationStatus.NotQualified,
        title: 'Not qualified',
        amount: details.notQualifiedCount,
        percentage: details.notQualifiedPercent,
        active: list.status === ChatbotConversationStatus.NotQualified,
        tooltipProps: {
          maxWidth: 351,
          label: "Contacts who haven't passed through qualification yet",
          placement: 'top',
        },
      },
    details.type !== ChatbotInstructionType.Unified && {
      key: ChatbotConversationStatus.Abandoned,
      title: 'Abandoned',
      amount: details.fallbackCount,
      percentage: details.fallbackPercent,
      active: list.status === ChatbotConversationStatus.Abandoned,
      tooltipProps: {
        label: 'Conversation ended before reaching the goal',
        placement: 'top',
      },
    },
    details.active && {
      key: ChatbotConversationStatus.Skipped,
      title: 'Skipped',
      amount: details.skippedCount,
      percentage: details.skippedPercent,
      active: list.status === ChatbotConversationStatus.Skipped,
      tooltipProps: {
        label: 'Skipped contacts currently active in another AI Agent',
        placement: 'top',
        maxWidth: 'unset',
      },
    },
    {
      key: ChatbotConversationStatus.Completed,
      title: details.type === 4 ? 'Booked' : 'Completed',
      amount: details.completedCount,
      percentage: details.completedPercent,
      active: list.status === ChatbotConversationStatus.Completed,
      tooltipProps: {
        label:
          details.type === 4
            ? 'Contacts who booked a meeting'
            : 'Contacts who finished the conversation',
        placement: 'top',
      },
    },
    (details.isBookingEnabled || details.unifiedBookingCount > 0) && {
      key: ChatbotConversationStatus.UnifiedBooked,
      title: 'Booked',
      amount: details.unifiedBookingCount,
      percentage: details.unifiedBookingPercent,
      active: list.status === ChatbotConversationStatus.UnifiedBooked,
      tooltipProps: {
        label: 'Contacts who booked a meeting',
        placement: 'top',
      },
    },
    ((details.isBookingEnabled && details.smartBookingEnabled) || details.canceledCount > 0) && {
      key: ChatbotConversationStatus.Canceled,
      title: 'Canceled',
      amount: details.canceledCount,
      percentage: details.canceledPercent,
      active: list.status === ChatbotConversationStatus.Canceled,
      tooltipProps: {
        label: 'Contacts who canceled their meeting',
        placement: 'top',
      },
    },
    ((details.isBookingEnabled && details.smartBookingEnabled) || details.rescheduledCount > 0) && {
      key: ChatbotConversationStatus.Rescheduled,
      title: 'Rescheduled',
      amount: details.rescheduledCount,
      percentage: details.rescheduledPercent,
      active: list.status === ChatbotConversationStatus.Rescheduled,
      tooltipProps: {
        label: 'Contacts who rescheduled their meeting',
        placement: 'top',
      },
    },
    details.isHandoffEnabled && {
      key: ChatbotConversationStatus.HandoffToHuman,
      title: 'Handed off',
      amount: details.handoffContactsCount,
      percentage: details.handoffContactsPercent,
      active: list.status === ChatbotConversationStatus.HandoffToHuman,
      tooltipProps: {
        label: 'Contacts handed off to human',
        placement: 'top',
      },
    },
    {
      key: ChatbotConversationStatus.Terminated,
      title: 'Stopped by user',
      amount: details.terminatedCount,
      percentage: details.terminatedPercent,
      active: list.status === ChatbotConversationStatus.Terminated,
      tooltipProps: {
        label: 'Sessions stopped by user',
        placement: 'top',
      },
    },
    ...(quietHoursFilter ? [quietHoursFilter] : []),
  ]

  return (
    <section className={styles.filter}>
      <CardsFilter
        onActiveFilter={(active) => list.changeStatus(active.key)}
        items={allItems.filter(truthy)}
      />
    </section>
  )
})
